import { useState, useCallback } from 'react';
import { X, Upload, FileSpreadsheet, ArrowLeft, AlertTriangle, Info, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useImportPreviewJSON, useImportPreviewXLSX, useImportConfirm } from '../../hooks/useSpecifications';
import type { ImportResult, ImportTC } from '@maios/shared';
import Spinner from '../ui/Spinner';

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const TYPE_COLOR: Record<string, string> = {
  POSITIVE: 'bg-green-100 text-green-800',
  NEGATIVE: 'bg-red-100 text-red-800',
  BOUNDARY: 'bg-purple-100 text-purple-800',
  ALTERNATIVE: 'bg-blue-100 text-blue-800',
};

const PRIORITY_LABEL: Record<string, string> = {
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

const CATEGORY_LABEL: Record<string, string> = {
  FLOW: '흐름',
  DETAIL_SPEC: '상세명세',
  BUSINESS_RULE: '규칙',
  CROSS_SCENARIO: '교차',
  EXCEPTION: '예외',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

interface Props {
  projectId: string;
  specId: string;
  onClose: () => void;
}

export default function TCImportModal({ projectId, specId, onClose }: Props) {
  // Step state
  const [step, setStep] = useState<'input' | 'preview'>('input');

  // Step 1 state
  const [jsonText, setJsonText] = useState('');
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);

  // Step 2 state
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  // Mutations
  const previewJsonMutation = useImportPreviewJSON(projectId);
  const previewXlsxMutation = useImportPreviewXLSX(projectId);
  const confirmMutation = useImportConfirm(projectId);

  const isPreviewing = previewJsonMutation.isPending || previewXlsxMutation.isPending;

  // --- Step 1 Handlers ---

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const name = file.name.toLowerCase();

    if (name.endsWith('.json')) {
      // JSON 파일 → 텍스트로 읽어서 textarea에 표시
      setXlsxFile(null);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setJsonText(ev.target?.result as string || '');
      };
      reader.readAsText(file);
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      // XLSX 파일 → state에 저장
      setXlsxFile(file);
    } else {
      toast.error('지원하지 않는 파일 형식입니다. .json 또는 .xlsx 파일을 선택해주세요.');
    }

    // input 초기화 (같은 파일 재선택 가능하도록)
    e.target.value = '';
  }, []);

  const handlePreview = useCallback(async () => {
    try {
      let result: ImportResult;

      if (xlsxFile) {
        // XLSX 우선
        result = await previewXlsxMutation.mutateAsync({ specId, file: xlsxFile });
      } else if (jsonText.trim()) {
        // JSON 텍스트
        result = await previewJsonMutation.mutateAsync({ specId, jsonContent: jsonText });
      } else {
        toast.error('JSON을 입력하거나 파일을 선택해주세요.');
        return;
      }

      setImportResult(result);
      // 기본: 모든 유효 TC 선택
      setSelectedIndices(new Set(result.valid.map((_, i) => i)));
      setStep('preview');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '미리보기에 실패했습니다.');
    }
  }, [xlsxFile, jsonText, specId, previewXlsxMutation, previewJsonMutation]);

  // --- Step 2 Handlers ---

  const toggleSelect = useCallback((idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!importResult) return;
    if (selectedIndices.size === importResult.valid.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(importResult.valid.map((_, i) => i)));
    }
  }, [importResult, selectedIndices.size]);

  const handleConfirm = useCallback(async () => {
    if (!importResult || selectedIndices.size === 0) return;

    const selected: ImportTC[] = importResult.valid.filter((_, i) => selectedIndices.has(i));

    try {
      const res = await confirmMutation.mutateAsync({ specId, testCases: selected });
      toast.success(`${res.imported}개 테스트케이스가 Import되었습니다.`);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Import에 실패했습니다.');
    }
  }, [importResult, selectedIndices, specId, confirmMutation, onClose]);

  const handleBack = useCallback(() => {
    setStep('input');
    setImportResult(null);
    setSelectedIndices(new Set());
  }, []);

  // --- Render ---

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {step === 'input' ? 'TC Import' : 'TC Import 미리보기'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 'input' ? (
            <StepInput
              jsonText={jsonText}
              setJsonText={setJsonText}
              xlsxFile={xlsxFile}
              setXlsxFile={setXlsxFile}
              onFileChange={handleFileChange}
            />
          ) : importResult ? (
            <StepPreview
              result={importResult}
              selectedIndices={selectedIndices}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
            />
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          {step === 'input' ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handlePreview}
                disabled={isPreviewing || (!jsonText.trim() && !xlsxFile)}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPreviewing && <Spinner className="size-4 text-white" />}
                미리보기
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="size-4" />
                돌아가기
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirmMutation.isPending || selectedIndices.size === 0}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {confirmMutation.isPending && <Spinner className="size-4 text-white" />}
                선택항목 Import ({selectedIndices.size}개)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// Step 1: 입력
// ─────────────────────────────────────────────────────

interface StepInputProps {
  jsonText: string;
  setJsonText: (v: string) => void;
  xlsxFile: File | null;
  setXlsxFile: (f: File | null) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function StepInput({ jsonText, setJsonText, xlsxFile, setXlsxFile, onFileChange }: StepInputProps) {
  return (
    <div className="space-y-5">
      {/* 안내 문구 */}
      <p className="text-sm text-gray-600">
        Cowork에서 생성된 TC를 가져옵니다. JSON 또는 엑셀(xlsx) 파일을 지원합니다.
      </p>

      {/* 방법 1: JSON 붙여넣기 */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">방법 1: JSON 붙여넣기</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
        <textarea
          value={jsonText}
          onChange={(e) => {
            setJsonText(e.target.value);
            if (e.target.value.trim()) setXlsxFile(null); // JSON 입력 시 xlsx 해제
          }}
          placeholder={'[\n  {\n    "title": "로그인 성공",\n    "steps": [...],\n    "expectedResult": "대시보드 이동"\n  }\n]'}
          rows={10}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono text-gray-800 placeholder-gray-400 resize-y focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          disabled={!!xlsxFile}
        />
      </div>

      {/* 방법 2: 파일 업로드 */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">방법 2: 파일 업로드</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
            <Upload className="size-4" />
            파일 선택 (.json, .xlsx)
            <input
              type="file"
              accept=".json,.xlsx,.xls"
              onChange={onFileChange}
              className="hidden"
            />
          </label>
          {xlsxFile ? (
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="size-4 text-green-600" />
              <span className="text-green-700 font-medium">
                엑셀 파일 선택됨: {xlsxFile.name} ({formatFileSize(xlsxFile.size)})
              </span>
              <button
                onClick={() => setXlsxFile(null)}
                className="p-0.5 text-gray-400 hover:text-red-500"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <span className="text-sm text-gray-400">선택된 파일: 없음</span>
          )}
        </div>
      </div>

      {/* 안내 */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
        <Info className="size-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium mb-1">다양한 형식을 자동으로 인식합니다.</p>
          <ul className="space-y-0.5 text-blue-600">
            <li>JSON: 한국어/영어 키, camelCase/snake_case</li>
            <li>XLSX: 컬럼 헤더 자동 매핑, 섹션 헤더 행 자동 필터링</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// Step 2: 미리보기
// ─────────────────────────────────────────────────────

interface StepPreviewProps {
  result: ImportResult;
  selectedIndices: Set<number>;
  onToggleSelect: (idx: number) => void;
  onToggleSelectAll: () => void;
}

function StepPreview({ result, selectedIndices, onToggleSelect, onToggleSelectAll }: StepPreviewProps) {
  const { valid, invalid, warnings, summary, xlsxInfo } = result;

  return (
    <div className="space-y-4">
      {/* XLSX 정보 */}
      {xlsxInfo && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg text-sm text-green-700">
          <FileSpreadsheet className="size-4" />
          <span>
            <span className="font-medium">{xlsxInfo.sheetName}</span> 시트에서{' '}
            {xlsxInfo.totalRows}행 중 <span className="font-medium">{xlsxInfo.filteredRows}행</span> 추출
          </span>
        </div>
      )}

      {/* 요약 바 */}
      <div className="flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
          <Check className="size-4" />
          {summary.valid}개 유효
        </span>
        {summary.invalid > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
            <AlertTriangle className="size-4" />
            {summary.invalid}개 오류
          </span>
        )}
        <span className="text-gray-500">총 {summary.total}개 감지</span>
      </div>

      {/* 타입별 배지 */}
      {Object.keys(summary.byType).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm text-gray-500 mr-1">타입:</span>
          {Object.entries(summary.byType).map(([type, count]) => (
            <span
              key={type}
              className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${TYPE_COLOR[type] || 'bg-gray-100 text-gray-700'}`}
            >
              {TYPE_LABEL[type] || type} {count}
            </span>
          ))}
        </div>
      )}

      {/* 관점별 배지 */}
      {Object.keys(summary.byCategory).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm text-gray-500 mr-1">관점:</span>
          {Object.entries(summary.byCategory).map(([cat, count]) => (
            <span
              key={cat}
              className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-gray-100 text-gray-700"
            >
              {CATEGORY_LABEL[cat] || cat} {count}
            </span>
          ))}
        </div>
      )}

      {/* 유효 TC 목록 */}
      {valid.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <input
              type="checkbox"
              checked={selectedIndices.size === valid.length && valid.length > 0}
              onChange={onToggleSelectAll}
              className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              전체 선택 ({valid.length})
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
            {valid.map((tc, i) => (
              <label
                key={i}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedIndices.has(i)}
                  onChange={() => onToggleSelect(i)}
                  className="size-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${TYPE_COLOR[tc.type] || 'bg-gray-100 text-gray-700'}`}>
                      {TYPE_LABEL[tc.type] || tc.type}
                    </span>
                    <span className="text-sm text-gray-900 truncate">{tc.title}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
                    <span>{PRIORITY_LABEL[tc.priority] || tc.priority}</span>
                    {tc.category && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span>{CATEGORY_LABEL[tc.category] || tc.category}</span>
                      </>
                    )}
                    {tc.coverageTarget && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="truncate">근거: {tc.coverageTarget}</span>
                      </>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 매핑 안내 */}
      {warnings.length > 0 && (
        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
          <p className="text-sm font-medium text-amber-700 mb-1.5">매핑 안내</p>
          <ul className="space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i} className="text-sm text-amber-600 flex items-start gap-1">
                <span className="shrink-0">-</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 오류 TC */}
      {invalid.length > 0 && (
        <div className="border border-red-200 rounded-lg p-3 bg-red-50">
          <p className="text-sm font-medium text-red-700 mb-1.5">
            오류 TC (Import 불가) — {invalid.length}건
          </p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {invalid.map((item, i) => (
              <div key={i} className="text-sm text-red-600">
                <span className="font-mono">{i + 1}.</span>{' '}
                <span>{item.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
